import { Trans } from '@lingui/macro'
import { Dialog, DialogContent } from '@mui/material'
import { DialogTitleWithIconClose } from '../DialogTitleWithIconClose'
import { ContactFieldsSearch } from './ContactFieldsSearch'
import { ContactFieldsSearchResults } from './ContactFieldsSearchResults'
import { ContactFieldsUpdate } from './ContactFieldsUpdate'
import { ContactFieldsInfo } from './ContactFieldsInfo'
import { ContactFieldsAddContact } from './ContactFieldsAddContact'
import { ContactFieldsErrorResults } from './ContactFieldsErrorResults'
import { ContactFieldsCreate } from './ContactFieldsCreate'
import { forwardRef, MouseEvent, useImperativeHandle } from 'react'
import { ContactParsed } from 'app/services/sfAuth/sfData/sfContact'
import { RootStateOrAny, useSelector } from 'react-redux'
import { ContactFieldsValues } from './ContactFields'
import { ContactDialogType, SearchContactsFn, useContactDialog } from './useContactDialog'
import ConfirmationDialog from 'app/views/forms/common/ConfirmationDialog'
import { AlertBanner } from 'app/views/common/alerts/AlertBanner'
import { ContactFieldsMissingInfo } from './ContactFieldsMissingInfo'

type ContactFieldsType =
  | 'contact-info'
  | 'update-contact'
  | 'missing-info'
  | 'add-contact'
  | 'search'
  | 'search-results'
  | 'error-results'
  | 'too-many-results'
  | 'new-contact'
  | undefined

export interface ContactSelectorDialogRef {
  open: (type: ContactFieldsType) => void
}

interface ContactSelectorDialogProps {
  onSelect?: (contact: ContactParsed) => void
  onDialogClose?: (e: MouseEvent) => void
  disabled?: boolean
  preview?: boolean
  values?: ContactParsed
  searchContacts: SearchContactsFn
  createNewContact: (contact: ContactParsed) => Promise<any>
  updateContact: (params: { contact: ContactFieldsValues, isValid: boolean, initialValues: ContactFieldsValues }) => Promise<any>
  currentOrganizationAsDefault?: boolean
  type?: ContactDialogType
  role?: string
}

export const ContactSelectorDialog = forwardRef<ContactSelectorDialogRef, ContactSelectorDialogProps>(({
  onSelect,
  onDialogClose,
  disabled,
  preview,
  values,
  searchContacts,
  createNewContact,
  updateContact,
  currentOrganizationAsDefault = false,
  type = 'contact-picker',
  role
}, ref) => {
  const { state, actions } = useContactDialog({ onSelect, searchContacts, createNewContact, updateContact })

  const {
    dialogOpened,
    searchResult,
    loading,
    searchFirstName,
    searchLastName,
    searchEmail,
    addContactValues,
    contactFieldsType,
    confirmSwitchDialogOpen
  } = state

  const {
    handleReturn,
    confirmSelection,
    handleSearch,
    handleUpdate,
    handleAddContact,
    handleEdit,
    handleSwitchContact,
    handleClose,
    handleModifySearch,
    resultsCreateNewContact,
    handleCreateNewContact,
    openDialogWithType,
    handleConfirmSwitchContact,
    handleContinueWithSearch
  } = actions

  const organization = useSelector((state: RootStateOrAny) => state.organization)

  useImperativeHandle(ref, () => ({
    open: (type: ContactFieldsType) => {
      openDialogWithType(type)
    }
  }), [openDialogWithType])

  return (
    <>
      {contactFieldsType !== 'too-many-results' &&
        <Dialog
          open={dialogOpened}
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: 912 },
              maxWidth: { xs: '100%' },
              m: { xs: 1, sm: 2 }
            }
          }}
        >
          <DialogTitleWithIconClose
            handleClose={(e: MouseEvent) => {
              handleClose()
              if (onDialogClose != null) {
                onDialogClose(e)
              }
            }}
            disabled={disabled === true || loading}
          />
          <DialogContent sx={{ paddingTop: 0 }}>

            {contactFieldsType === 'contact-info' && values != null && (
              <ContactFieldsInfo
                loading={loading}
                values={values}
                onEdit={handleEdit}
                onSwitch={handleSwitchContact}
                onClose={handleClose}
                type={type}
                role={role}
              />)}

            {contactFieldsType === 'missing-info' && values != null && (
              <ContactFieldsMissingInfo
                loading={loading}
                values={values}
                onUpdate={({ values, isValid, initialValues }) => {
                  void handleUpdate({ values, isValid, initialValues }).catch(console.error)
                }}
                onSwitch={handleSwitchContact}
                onClose={handleClose}
                type={type}
                role={role}
              />)}

            {contactFieldsType === 'update-contact' && values != null && (
              <ContactFieldsUpdate
                loading={loading}
                values={values}
                onUpdate={({ values, isValid, initialValues }) => {
                  void handleUpdate({ values, isValid, initialValues }).catch(console.error)
                }}
                onSwitch={handleSwitchContact}
                onClose={handleClose}
                type={type}
                role={role}
              />)}

            {contactFieldsType === 'add-contact' && addContactValues != null && (
              <ContactFieldsAddContact
                loading={loading}
                values={addContactValues}
                onAdd={async ({ values, isValid, initialValues, dirty }) => {
                  await handleAddContact({ values, isValid, initialValues, dirty }).catch(console.error)
                }}
                onBack={handleReturn}
                type={type}
                role={role}
              />)}

            {contactFieldsType === 'search' && (
              <ContactFieldsSearch
                loading={loading}
                firstName={searchFirstName}
                lastName={searchLastName}
                email={searchEmail}
                onSearch={(values) => {
                  void handleSearch(values)
                }}
                onBack={handleReturn}
                type={type}
                role={role}
              />)}

            {contactFieldsType === 'search-results' && (
              <ContactFieldsSearchResults
                loading={loading}
                searchResult={searchResult}
                preview={Boolean(preview)}
                onConfirmSelection={confirmSelection}
                onModifySearch={handleModifySearch}
                onCreateNewContact={resultsCreateNewContact}
                type={type}
                role={role}
              />
            )}

            {contactFieldsType === 'error-results' && (
              <ContactFieldsErrorResults
                loading={loading}
                searchResult={searchResult}
                preview={Boolean(preview)}
                onCreateNewContact={resultsCreateNewContact}
                onModifySearch={handleModifySearch}
                type={type}
                role={role}
              />
            )}

            {contactFieldsType === 'new-contact' && (
              <ContactFieldsCreate
                loading={loading}
                onCreate={async (params) => await handleCreateNewContact(params)}
                onBack={handleReturn}
                organizationName={currentOrganizationAsDefault ? organization.organisationsName : ''}
                type={type}
                role={role}
              />
            )}

            <ConfirmationDialog
              open={confirmSwitchDialogOpen}
              title={<Trans>CONFIRM_SWITCH_CONTACT_DIALOG_TITLE</Trans> as any}
              text={<Trans>CONFIRM_SWITCH_CONTACT_DIALOG_DESCRIPTION</Trans>}
              onYesClick={() => handleConfirmSwitchContact(true)}
              onConfirmDialogClose={() => handleConfirmSwitchContact(false)}
            />

          </DialogContent>
        </Dialog>}

      <ConfirmationDialog
        open={contactFieldsType === 'too-many-results'}
        title={<Trans>TOO_MANY_RESULTS_DIALOG_TITLE</Trans> as any}
        text={
          <AlertBanner
            description={<Trans>TOO_MANY_RESULTS_DIALOG_DESCRIPTION</Trans>}
            severity='warning'
            variant='standard'
            sx={{
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '16px',
              paddingBottom: '16px'
            }}
          />
        }
        yesTitle={<Trans>Continue</Trans>}
        noTitle={<Trans>CLOSE_BTN</Trans>}
        onYesClick={handleContinueWithSearch}
        onConfirmDialogClose={handleClose}
      />

    </>
  )
})

ContactSelectorDialog.displayName = 'ContactSelectorDialog'
